import { MinusCircleOutlined } from '@ant-design/icons';
import { Think } from '@ant-design/x';
import { XMarkdown } from '@ant-design/x-markdown';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useStreamingReveal } from '../../hooks/chat/useStreamingReveal';
import type { AiChatDocumentMessagePayload } from '../../interfaces/AiChatDocumentMessagePayload';
import { mapChatErrorMessage } from '../../lib/chat/mapChatErrorMessage';
import { ASSISTANT_STREAMING } from './assistantStreaming';
import { resolveStreamActivities, StreamToolsPanel } from './StreamToolsPanel';

type Props = {
  message: AiChatDocumentMessagePayload;
  isUser: boolean;
  isError?: boolean;
  /**
   * Copy / regenerate / TTS. Mounted only when Done and reveal caught up —
   * never during Pending / mid-reveal (avoids footer jump while height grows).
   */
  actions?: ReactNode;
};

/** Assistant/user body for document chat — tools panel + reveal + XMarkdown stream. */
export function DocumentChatMessageItem({ message, isUser, isError, actions }: Props) {
  const { t } = useTranslation();
  const isPending = message.status === 'Pending' && !isError;
  const isCancelled = message.status === 'Cancel';
  // SignalR / finalize text = target. active only while Pending (cold-start empty);
  // Done settle keeps typewriter from current length (complete-only + streamed).
  const revealedContent = useStreamingReveal(message.content, !isUser && isPending && !isError);
  const assistantCatchingUp = !isUser && !isError && revealedContent !== message.content;
  const streamActivities = !isUser ? resolveStreamActivities(message) : [];

  if (isError) {
    const { body } = mapChatErrorMessage(message.content, t);
    return <>{body}</>;
  }

  if (isUser) {
    return <>{message.content}</>;
  }

  if (isCancelled) {
    return (
      <div className="flex items-center gap-2">
        <MinusCircleOutlined className="text-neutral-400" aria-hidden />
        <span className="text-sm text-neutral-400 italic dark:text-neutral-500">
          {t('chat.requestCancelled')}
        </span>
      </div>
    );
  }

  if (isPending) {
    const hasPartial = message.content.length > 0 || revealedContent.length > 0;

    return (
      <div className="flex w-full flex-col gap-2" role="status" aria-live="polite">
        {streamActivities.length > 0 ? (
          <StreamToolsPanel t={t} activities={streamActivities} defaultExpanded />
        ) : !hasPartial ? (
          <Think loading blink title={t('chat.thinking.working')} defaultExpanded />
        ) : null}
        {hasPartial ? (
          <XMarkdown streaming={ASSISTANT_STREAMING}>{revealedContent}</XMarkdown>
        ) : null}
      </div>
    );
  }

  return (
    <div className="text-blue-dark w-full">
      {streamActivities.length > 0 ? (
        <div className="mb-2.5">
          <StreamToolsPanel t={t} activities={streamActivities} defaultExpanded={false} />
        </div>
      ) : null}
      <XMarkdown streaming={assistantCatchingUp ? ASSISTANT_STREAMING : undefined}>
        {revealedContent}
      </XMarkdown>
      {/* Outside Bubble footer: share reveal state so actions never mount mid-typewriter. */}
      {!assistantCatchingUp && actions ? (
        <div className="mt-1" data-testid="document-chat-message-actions">
          {actions}
        </div>
      ) : null}
    </div>
  );
}

export default DocumentChatMessageItem;
